import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Button, DateRangePicker, PageHeader, Table } from '@demandlocal/components'
import LineChart from '../../../../../components/Charts/LineChart'
import { ReactComponent as DownloadIcon } from '../../../../../images/icons/action_download.svg'
import { useParams } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import { useDownloadPDFReport } from '../../../../../api/dealership/reports/useDownloadPDFReport'
import toast from 'react-hot-toast'
import { DateRange } from '../../../../../lib/types/DateRange'
import { GA4 } from '../../../../../lib/utils/ga4'
import { useDealership } from '../../../../../store/zustand/dealershipStore'
import mtm from '../../../../../lib/utils/mtm'
import { useDateRange } from '../../../../../store/zustand/dateRangeStore'
import { useGetSelfServiceCampaignMetrics } from '../../../../../api/dealership/campaigns/self-service/useGetSelfServiceCampaignMetrics'
import { useGetSelfServiceCampaignById } from '../../../../../api/dealership/campaigns/self-service/useGetSelfServiceCampaignById'
import { useTranslation } from 'react-i18next'
import { formatNumber, formatPercentage } from '../../../../../lib/utils/formatNumber'
import {
  SelfServiceProduct,
  useGetSelfServiceCampaignProducts,
} from '../../../../../api/dealership/campaigns/self-service/useGetSelfServiceCampaignProducts'
import { createColumnHelper } from '@tanstack/react-table'
import downloadCSV from '../../../../../api/downloadCSV'

const productColumnHelper = createColumnHelper<SelfServiceProduct>()
const CampaignTemplate = ({
  children,
  filters,
  setFilters,
}: PropsWithChildren<{
  filters: DateRange
  setFilters: Dispatch<SetStateAction<DateRange>>
}>): JSX.Element => {
  const { campaign_id } = useParams()
  const dealership = useDealership()
  const { t } = useTranslation('routes')
  const { setDateRange } = useDateRange()
  const { data: campaign } = useGetSelfServiceCampaignById({ campaign_id })

  const { data, isLoading, isPreviousData } = useGetSelfServiceCampaignMetrics({
    filters: filters.dateRange,
    campaign_id: campaign_id,
  })

  const { mutateAsync: downloadReport, isLoading: isDownloadingReport } =
    useDownloadPDFReport()

  const onDateRangeChange = (dateRange: DateRange): void => {
    setFilters((prev: DateRange) => ({
      ...prev,
      ...dateRange,
    }))
    setDateRange(dateRange)
  }

  const overallMetrics = useMemo(() => {
    const totals = data?.reduce(
      (acc, curr) => ({
        totalClicks: acc.totalClicks + curr.clicks,
        totalImpressions: acc.totalImpressions + curr.impressions,
      }),
      { totalClicks: 0, totalImpressions: 0 },
    )

    return {
      impressions: totals?.totalImpressions || 0,
      clicks: totals?.totalClicks || 0,
      ctr:
        totals?.totalImpressions && totals?.totalImpressions > 0
          ? totals?.totalClicks / totals?.totalImpressions
          : 0,
    }
  }, [data])

  useEffect(() => {
    if (!isLoading) {
      const content = document.getElementById('content')
      if (content) content.className = `${content.className} loaded`
    }
  }, [isLoading])

  return (
    <div className={'flex flex-1 flex-col'} id={'content'}>
      <PageHeader
        backRoute={t('dealership.campaigns.self-service.list', { dealership })}
        title={`${dealership?.dealership} - ${campaign?.vendor_campaign?.name ?? ''}`}
        cardsOptions={[
          {
            title: 'CTR',
            content: formatPercentage(overallMetrics?.ctr, 2),
            color: 'bg-palatinate-blue/30',
          },
          {
            title: 'Total Impressions',
            content: formatNumber(overallMetrics.impressions),
            color: 'bg-kelly-green/30',
          },
          {
            title: 'Total clicks',
            content: formatNumber(overallMetrics.clicks),
            color: 'bg-dodger-blue/30',
          },
        ]}
        rightSlot={({ hidden }) => (
          <div className={'print:hidden flex flex-row gap-2'}>
            <DateRangePicker
              collapsed={hidden}
              filters={filters}
              onChange={onDateRangeChange}
            />
            <Button
              disabled={isDownloadingReport}
              collapsed={hidden}
              leftSlot={<DownloadIcon className={'fill-white'} />}
              onClick={async (e) => {
                e.preventDefault()
                const toastId = toast.loading('Generating report...')
                GA4.sendEvent({
                  event_name: 'export_pdf',
                  event_params: {
                    campaign_type: 'self_service',
                    campaign_id: campaign?._id,
                  },
                })
                mtm.sendEvent('Export', 'PDF', 'AUD', campaign?._id)
                await downloadReport(undefined, {
                  async onSuccess(data) {
                    toast.success('Report generated successfully', { id: toastId })
                    const blob = await data.blob()
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `${campaign?.vendor_campaign?.name}_${filters?.dateRange?.startDate}_-_${filters?.dateRange?.endDate}.pdf`
                    document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
                    a.click()
                    a.remove()
                  },
                })
              }}>
              Export as PDF
            </Button>
          </div>
        )}
      />
      <div className={'flex flex-1 flex-col px-10 max-w-[1920px] mx-auto w-full'}>
        <div className={'mt-12 mb-10'} />
        {children}
      </div>
    </div>
  )
}

const SelfServiceCampaign = (): JSX.Element => {
  const { campaign_id } = useParams()
  const { data: campaign } = useGetSelfServiceCampaignById({ campaign_id })
  const dealership = useDealership()
  const [queryParams, setQueryParams] = useSearchParams()
  const { dateRange } = useDateRange()
  const [downloadingTable, setDownloadingTable] = useState<boolean>(false)

  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate: queryParams.get('startDate') || dateRange.dateRange.startDate,
      endDate: queryParams.get('endDate') || dateRange.dateRange.endDate,
    },
  })

  useEffect(() => {
    setQueryParams(filters.dateRange)
  }, [filters, setQueryParams])

  const { data, isLoading, isPreviousData } = useGetSelfServiceCampaignMetrics({
    filters: filters.dateRange,
    campaign_id: campaign_id,
  })

  const productColumns = useMemo(
    () => [
      productColumnHelper.accessor('product_title', {
        header: 'Product',
        cell: (info) => info.getValue(),
      }),
      productColumnHelper.accessor('product_item_id', {
        header: 'Product ID',
        cell: (info) => info.getValue(),
      }),
      productColumnHelper.accessor('impressions', {
        header: 'Impressions',
        cell: (info) => formatNumber(info.getValue() || 0),
      }),
      productColumnHelper.accessor('clicks', {
        header: 'Clicks',
        cell: (info) => formatNumber(info.getValue() || 0),
      }),
      productColumnHelper.accessor('conversions', {
        header: 'Conversions',
        cell: (info) => formatNumber(Math.ceil(info.getValue()) || 0),
      }),
    ],
    [],
  )

  const { data: { pagination: products } = {} } = useGetSelfServiceCampaignProducts({
    filters: { campaign_id, ...filters.dateRange },
    pageSize: 1,
  })
  const hasProducts = useMemo(() => Number(products?.total) > 0, [products?.total])

  return (
    <CampaignTemplate filters={filters} setFilters={setFilters}>
      <div className={'flex flex-col gap-10 pb-8'}>
        <div className={'flex flex-col gap-8'}>
          <div className={'flex grid grid-cols-2 gap-5'}>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Total Impressions</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <LineChart
                  data={data ?? []}
                  gradient={'green-to-cyan'}
                  yKey={'impressions'}
                  isLoading={isLoading}
                  isPreviousData={isPreviousData}
                />
              </div>
            </div>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Total Clicks</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <LineChart
                  data={data ?? []}
                  gradient={'red-to-orange'}
                  yKey={'clicks'}
                  isLoading={isLoading}
                  isPreviousData={isPreviousData}
                />
              </div>
            </div>
          </div>
          <div className={'min-h-[300px] border-2 rounded-xl'}>
            <div
              className={
                'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
              }>
              <span>Click Through Rate</span>
            </div>
            <div className={'h-[270px] p-2'}>
              <LineChart
                data={
                  data?.map((d) => ({
                    date: d.date,
                    ctr: d.impressions > 0 ? d.clicks / d.impressions : 0,
                  })) ?? []
                }
                gradient={'blue-to-purple'}
                yKey={'ctr'}
                isLoading={isLoading}
                isPreviousData={isPreviousData}
              />
            </div>
          </div>
          {hasProducts && (
            <div className="flex flex-col gap-3 pt-3">
              <div className="flex flex-row justify-between items-center">
                <div className="flex flex-row gap-2 items-center">
                  <span className="font-bold text-sm">Product Performance</span>
                </div>
                <Button
                  disabled={downloadingTable}
                  variant="primary"
                  onClick={async (e) => {
                    e.preventDefault()
                    const toastId = toast.loading('Downloading...')
                    setDownloadingTable(true)
                    try {
                      const headers = productColumns.map((column) => ({
                        // @ts-ignore
                        accessorKey: column.accessorKey,
                        header:
                          typeof column.header === 'function'
                            ? column.accessorKey
                            : column.header,
                      }))
                      await downloadCSV({
                        url: `/agencies/${dealership?.organization_id}/dealership/${dealership?.id}/campaigns/self-service/${campaign_id}/products`,
                        fetchOptions: {
                          campaign_id,
                          ...filters.dateRange,
                        },
                        headers: headers,
                        fileName: `${campaign.vendor_campaign.name}-ProductPerformance_${filters.dateRange.startDate}-${filters.dateRange.endDate}.csv`,
                      })
                      toast.success('Downloaded table!', {
                        id: toastId,
                        duration: 5000,
                      })
                      setDownloadingTable(false)
                    } catch (err) {
                      toast.error('Failed to download table at this time.', {
                        id: toastId,
                        duration: 5000,
                      })
                      setDownloadingTable(false)
                    }
                  }}>
                  Export CSV
                </Button>
              </div>
              <Table
                columns={productColumns}
                fetchFn={useGetSelfServiceCampaignProducts}
                filters={{
                  campaign_id: campaign_id,
                  ...filters.dateRange,
                }}
                defaultSorting={[{ id: 'clicks', desc: true }]}
              />
            </div>
          )}
        </div>
      </div>
    </CampaignTemplate>
  )
}

export default SelfServiceCampaign
